import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useEffect, useCallback, useRef } from 'react';
import { transactionAPI, categoryAPI, accountAPI, recurringTransactionAPI, insightsAPI, contactAPI, receivableAPI, debtAPI, paymentAPI, notificationAPI } from '../services/api';
import { logTransaction } from '../utils/facebookEvents';
import { ga } from '../services/ga';

// Common invalidation key groups — reused across mutations
const TXN_RELATED_KEYS = [
  'transactions', 'transaction', 'summary', 'categoryBreakdown',
  'calendar', 'stats', 'accountsWithBalances', 'budgetAnalysis',
  'budgetAnalysisSummary', 'ledger', 'monthlyTrend'
];

const ACCOUNT_KEYS = ['accounts', 'accountsWithBalances', 'account'];
const CATEGORY_KEYS = ['categories', 'category'];
const DEBT_KEYS = ['debts', 'debt', 'debtsSummary'];
const RECEIVABLE_KEYS = ['receivables', 'receivable', 'receivablesSummary'];
const CONTACT_KEYS = ['contacts', 'contact', 'contactHistory'];

/** Invalidate multiple query key groups */
const invalidateKeys = (queryClient, keys) =>
  Promise.all(keys.map(key => queryClient.invalidateQueries({ queryKey: [key] })));

// Query Keys
export const queryKeys = {
  // Use explicit key parts to ensure proper cache invalidation
  transactions: (params) => ['transactions', params.page, params.limit, params.type, params.category, params.account, params.financialYear, params.search, params.startDate, params.endDate, params.sortBy, params.sortOrder],
  transaction: (id) => ['transaction', id],
  summary: (params) => ['summary', params],
  categoryBreakdown: (params) => ['categoryBreakdown', params],
  financialYears: ['financialYears'],
  calendarData: (year, month) => ['calendar', year, month],
  monthlyTrend: (params) => ['monthlyTrend', params],
  stats: (params) => ['stats', params],
  ledger: (params) => ['ledger', params],
  categories: ['categories'],
  category: (id) => ['category', id],
  accounts: ['accounts'],
  account: (id) => ['account', id],
  accountsWithBalances: ['accountsWithBalances'],
  recurringTransactions: (params) => ['recurringTransactions', params],
  recurringTransaction: (id) => ['recurringTransaction', id],
  insights: (params) => ['budgetAnalysis', 'v2', params],
  insightsSummary: ['budgetAnalysisSummary', 'v2'],
  // Contacts, Receivables, Debts, Payments
  contacts: (params) => ['contacts', params],
  contact: (id) => ['contact', id],
  contactHistory: (id) => ['contactHistory', id],
  receivables: (params) => ['receivables', params],
  receivable: (id) => ['receivable', id],
  receivablesSummary: ['receivablesSummary'],
  debts: (params) => ['debts', params],
  debt: (id) => ['debt', id],
  debtsSummary: ['debtsSummary'],
  payments: (params) => ['payments', params],
  payment: (id) => ['payment', id],
  // Notifications
  notifications: (params) => ['notifications', params],
  notification: (id) => ['notification', id],
  notificationUnreadCount: ['notificationUnreadCount']
};

// ============ TRANSACTION HOOKS ============

export function useTransactionsQuery(params = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.transactions(params),
    queryFn: async () => {
      const { data } = await transactionAPI.getAll(params);
      return data.data;
    },
    placeholderData: keepPreviousData, // Show cached data instantly while fetching
    ...options
  });
}

export function useTransactionQuery(id, options = {}) {
  return useQuery({
    queryKey: queryKeys.transaction(id),
    queryFn: async () => {
      const { data } = await transactionAPI.getById(id);
      return data.data.transaction;
    },
    enabled: !!id,
    ...options
  });
}

export function useSummaryQuery(params = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.summary(params),
    queryFn: async () => {
      const { data } = await transactionAPI.getSummary(params);
      return data.data.summary;
    },
    placeholderData: keepPreviousData,
    ...options
  });
}

export function useCategoryBreakdownQuery(params = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.categoryBreakdown(params),
    queryFn: async () => {
      const { data } = await transactionAPI.getCategoryBreakdown(params);
      return data.data;
    },
    placeholderData: keepPreviousData,
    ...options
  });
}

export function useFinancialYearsQuery(options = {}) {
  return useQuery({
    queryKey: queryKeys.financialYears,
    queryFn: async () => {
      const { data } = await transactionAPI.getFinancialYears();
      return data.data.financialYears || [];
    },
    placeholderData: keepPreviousData,
    ...options
  });
}

export function useCalendarDataQuery(year, month, options = {}) {
  return useQuery({
    queryKey: queryKeys.calendarData(year, month),
    queryFn: async () => {
      const { data } = await transactionAPI.getCalendarData(year, month);
      return data.data;
    },
    // Don't use keepPreviousData for calendar - each month should show its own cached data
    // This prevents showing December data when navigating to November
    enabled: !!year && !!month,
    ...options
  });
}

// Helper to get prev/next month
function getAdjacentMonth(year, month, offset) {
  const date = new Date(year, month - 1 + offset, 1);
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
}

// Hook to prefetch adjacent months for instant swiping on mobile
// Debounces API calls when user rapidly swipes through months
export function usePrefetchCalendarMonths(year, month, enabled = true) {
  const queryClient = useQueryClient();
  const debounceTimerRef = useRef(null);

  const prefetchMonth = useCallback(async (y, m) => {
    const key = queryKeys.calendarData(y, m);
    // Only prefetch if not already in cache
    const existing = queryClient.getQueryData(key);
    if (!existing) {
      await queryClient.prefetchQuery({
        queryKey: key,
        queryFn: async () => {
          const { data } = await transactionAPI.getCalendarData(y, m);
          return data.data;
        },
        staleTime: 30 * 60 * 1000 // 30 minutes
      });
    }
  }, [queryClient]);

  useEffect(() => {
    if (!enabled || !year || !month) return;

    // Clear any pending debounce timer when month changes rapidly
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce: wait 300ms after user stops swiping before prefetching
    debounceTimerRef.current = setTimeout(() => {
      // Prefetch prev and next 2 months for smooth swiping
      const monthsToPreload = [-2, -1, 1, 2];

      // Stagger prefetch to avoid blocking current month
      monthsToPreload.forEach((offset, index) => {
        setTimeout(() => {
          const { year: y, month: m } = getAdjacentMonth(year, month, offset);
          prefetchMonth(y, m);
        }, 50 * (index + 1)); // 50ms, 100ms, 150ms, 200ms delays
      });
    }, 300); // Wait 300ms after user stops swiping

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [year, month, enabled, prefetchMonth]);
}

export function useStatsQuery(params = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.stats(params),
    queryFn: async () => {
      const { data } = await transactionAPI.getStats(params);
      return data.data;
    },
    placeholderData: keepPreviousData,
    ...options
  });
}

export function useMonthlyTrendQuery(params = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.monthlyTrend(params),
    queryFn: async () => {
      const { data } = await transactionAPI.getMonthlyTrend(params);
      return data.data; // { monthlyTrend, granularity }
    },
    placeholderData: keepPreviousData,
    ...options
  });
}

export function useLedgerQuery(params = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.ledger(params),
    queryFn: async () => {
      const { data } = await transactionAPI.getLedger(params);
      return data.data;
    },
    placeholderData: keepPreviousData,
    ...options
  });
}

export function useCreateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transactionData) => {
      const { data } = await transactionAPI.create(transactionData);
      const txn = data.data.transaction;
      logTransaction(txn.type, txn.amount, txn.category?.name || '');
      ga.addTransaction(txn.type);
      return txn;
    },
    onSuccess: () => invalidateKeys(queryClient, TXN_RELATED_KEYS)
  });
}

export function useUpdateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data: transactionData }) => {
      const { data } = await transactionAPI.update(id, transactionData);
      ga.editTransaction();
      return data.data.transaction;
    },
    onSuccess: () => invalidateKeys(queryClient, TXN_RELATED_KEYS)
  });
}

export function useDeleteTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await transactionAPI.delete(id);
      ga.deleteTransaction();
      return id;
    },
    onSuccess: async (deletedId) => {
      queryClient.removeQueries({ queryKey: ['transaction', deletedId] });
      await invalidateKeys(queryClient, TXN_RELATED_KEYS);
    }
  });
}

// ============ DESCRIPTION SUGGESTIONS HOOK ============

export function useDescriptionSuggestions() {
  const { data } = useQuery({
    queryKey: ['descriptionSuggestions'],
    queryFn: async () => {
      const { data } = await transactionAPI.getDescriptions();
      return data.data;
    },
    staleTime: 5 * 60 * 1000
  });
  return {
    descriptions: data?.descriptions || [],
    topCategories: data?.topCategories || [],
    topAccounts: data?.topAccounts || []
  };
}

// ============ CATEGORY HOOKS ============

export function useCategoriesQuery(options = {}) {
  return useQuery({
    queryKey: queryKeys.categories,
    queryFn: async () => {
      const { data } = await categoryAPI.getAll();
      return data.data.categories;
    },
    placeholderData: keepPreviousData,
    ...options
  });
}

export function useCategoryQuery(id, options = {}) {
  return useQuery({
    queryKey: queryKeys.category(id),
    queryFn: async () => {
      const { data } = await categoryAPI.getById(id);
      return data.data.category;
    },
    enabled: !!id,
    ...options
  });
}

export function useInitCategories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await categoryAPI.initDefaults();
      return data.data;
    },
    onSuccess: () => invalidateKeys(queryClient, [...CATEGORY_KEYS, ...TXN_RELATED_KEYS])
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (categoryData) => {
      const { data } = await categoryAPI.create(categoryData);
      ga.addCategory();
      return data.data.category;
    },
    onSuccess: () => invalidateKeys(queryClient, [...CATEGORY_KEYS, ...TXN_RELATED_KEYS])
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data: categoryData }) => {
      const { data } = await categoryAPI.update(id, categoryData);
      return data.data.category;
    },
    onSuccess: () => invalidateKeys(queryClient, [...CATEGORY_KEYS, ...TXN_RELATED_KEYS])
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await categoryAPI.delete(id);
      return id;
    },
    onSuccess: () => invalidateKeys(queryClient, [...CATEGORY_KEYS, ...TXN_RELATED_KEYS])
  });
}

export function useReorderCategories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ categoryIds, type }) => {
      const { data } = await categoryAPI.reorder(categoryIds, type);
      return { categories: data.data.categories, type };
    },
    onSuccess: ({ categories: reorderedCategories, type }) => {
      // Update cache directly without refetching (no loading state)
      queryClient.setQueryData(['categories'], (oldData) => {
        if (!oldData) return reorderedCategories;
        // Keep other type categories, replace reordered type
        const otherCategories = oldData.filter(c => c.type !== type);
        return [...otherCategories, ...reorderedCategories];
      });
    }
  });
}

// ============ ACCOUNT HOOKS ============

export function useAccountsQuery(params = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.accounts,
    queryFn: async () => {
      const { data } = await accountAPI.getAll(params);
      return data.data.accounts;
    },
    placeholderData: keepPreviousData,
    ...options
  });
}

export function useAccountsWithBalancesQuery(options = {}) {
  return useQuery({
    queryKey: queryKeys.accountsWithBalances,
    queryFn: async () => {
      const { data } = await accountAPI.getWithBalances();
      return data.data.accounts;
    },
    placeholderData: keepPreviousData,
    ...options
  });
}

export function useAccountQuery(id, options = {}) {
  return useQuery({
    queryKey: queryKeys.account(id),
    queryFn: async () => {
      const { data } = await accountAPI.getById(id);
      return data.data.account;
    },
    enabled: !!id,
    staleTime: 0, // Always fetch fresh data for edit form
    ...options
  });
}

export function useCreateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (accountData) => {
      const { data } = await accountAPI.create(accountData);
      ga.addAccount();
      return data.data;
    },
    onSuccess: () => invalidateKeys(queryClient, [...ACCOUNT_KEYS, ...TXN_RELATED_KEYS])
  });
}

export function useUpdateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data: accountData }) => {
      const { data } = await accountAPI.update(id, accountData);
      return { account: data.data.account, id };
    },
    onSuccess: () => invalidateKeys(queryClient, [...ACCOUNT_KEYS, ...TXN_RELATED_KEYS])
  });
}

export function useDeleteAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, permanent = false }) => {
      const { data } = await accountAPI.delete(id, permanent);
      return data;
    },
    onSuccess: () => invalidateKeys(queryClient, [...ACCOUNT_KEYS, ...TXN_RELATED_KEYS])
  });
}

export function useResetAccountBalance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, newBalance }) => {
      const { data } = await accountAPI.resetBalance(id, newBalance);
      return { ...data, id };
    },
    onSuccess: () => invalidateKeys(queryClient, [...ACCOUNT_KEYS, ...TXN_RELATED_KEYS])
  });
}

// ============ RECURRING TRANSACTION HOOKS ============

export function useRecurringTransactionsQuery(params = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.recurringTransactions(params),
    queryFn: async () => {
      const { data } = await recurringTransactionAPI.getAll(params);
      return data.data;
    },
    placeholderData: keepPreviousData,
    ...options
  });
}

export function useRecurringTransactionQuery(id, options = {}) {
  return useQuery({
    queryKey: queryKeys.recurringTransaction(id),
    queryFn: async () => {
      const { data } = await recurringTransactionAPI.getById(id);
      return data.data.recurringTransaction;
    },
    enabled: !!id,
    ...options
  });
}

export function useCreateRecurringTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const { data: response } = await recurringTransactionAPI.create(data);
      return response.data.recurringTransaction;
    },
    onSuccess: () => invalidateKeys(queryClient, ['recurringTransactions', 'recurringTransaction'])
  });
}

export function useUpdateRecurringTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const { data: response } = await recurringTransactionAPI.update(id, data);
      return response.data.recurringTransaction;
    },
    onSuccess: () => invalidateKeys(queryClient, ['recurringTransactions', 'recurringTransaction'])
  });
}

export function useDeleteRecurringTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await recurringTransactionAPI.delete(id);
      return id;
    },
    onSuccess: () => invalidateKeys(queryClient, ['recurringTransactions', 'recurringTransaction'])
  });
}

export function useToggleRecurringTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const { data } = await recurringTransactionAPI.toggle(id);
      return data.data.recurringTransaction;
    },
    onSuccess: () => invalidateKeys(queryClient, ['recurringTransactions', 'recurringTransaction'])
  });
}

// ============ INSIGHTS HOOKS ============

export function useInsightsQuery(params = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.insights(params),
    queryFn: async () => {
      const { data } = await insightsAPI.getAll(params);
      return data.data;
    },
    placeholderData: keepPreviousData,
    ...options
  });
}

export function useInsightsSummaryQuery(options = {}) {
  return useQuery({
    queryKey: queryKeys.insightsSummary,
    queryFn: async () => {
      const { data } = await insightsAPI.getSummary();
      return data.data;
    },
    placeholderData: keepPreviousData,
    ...options
  });
}

// ============ CONTACT HOOKS ============

export function useContactsQuery(params = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.contacts(params),
    queryFn: async () => {
      const { data } = await contactAPI.getAll(params);
      return data.data;
    },
    placeholderData: keepPreviousData,
    ...options
  });
}

export function useContactQuery(id, options = {}) {
  return useQuery({
    queryKey: queryKeys.contact(id),
    queryFn: async () => {
      const { data } = await contactAPI.getById(id);
      return data.data.contact;
    },
    enabled: !!id,
    ...options
  });
}

export function useContactHistoryQuery(id, options = {}) {
  return useQuery({
    queryKey: queryKeys.contactHistory(id),
    queryFn: async () => {
      const { data } = await contactAPI.getHistory(id);
      return data.data;
    },
    enabled: !!id,
    ...options
  });
}

export function useSearchContacts() {
  return useCallback(async (q) => {
    if (!q || q.length < 2) return [];
    const { data } = await contactAPI.search(q);
    return data.data.contacts;
  }, []);
}

export function useCreateContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contactData) => {
      const { data } = await contactAPI.create(contactData);
      return data.data.contact;
    },
    onSuccess: () => invalidateKeys(queryClient, CONTACT_KEYS)
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data: contactData }) => {
      const { data } = await contactAPI.update(id, contactData);
      return data.data.contact;
    },
    onSuccess: () => invalidateKeys(queryClient, [...CONTACT_KEYS, ...DEBT_KEYS, ...RECEIVABLE_KEYS])
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await contactAPI.delete(id);
      return id;
    },
    onSuccess: () => invalidateKeys(queryClient, [...CONTACT_KEYS, ...DEBT_KEYS, ...RECEIVABLE_KEYS])
  });
}

// ============ RECEIVABLE HOOKS ============

export function useReceivablesQuery(params = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.receivables(params),
    queryFn: async () => {
      const { data } = await receivableAPI.getAll(params);
      return data.data;
    },
    placeholderData: keepPreviousData,
    ...options
  });
}

export function useReceivableQuery(id, options = {}) {
  return useQuery({
    queryKey: queryKeys.receivable(id),
    queryFn: async () => {
      const { data } = await receivableAPI.getById(id);
      return data.data;
    },
    enabled: !!id,
    ...options
  });
}

export function useReceivablesSummaryQuery(options = {}) {
  return useQuery({
    queryKey: queryKeys.receivablesSummary,
    queryFn: async () => {
      const { data } = await receivableAPI.getSummary();
      return data.data?.summary || data.data || {};
    },
    placeholderData: keepPreviousData,
    ...options
  });
}

export function useCreateReceivable() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (receivableData) => {
      const { data } = await receivableAPI.create(receivableData);
      return data.data.receivable;
    },
    onSuccess: () => invalidateKeys(queryClient, [...RECEIVABLE_KEYS, ...CONTACT_KEYS])
  });
}

export function useUpdateReceivable() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data: receivableData }) => {
      const { data } = await receivableAPI.update(id, receivableData);
      return data.data.receivable;
    },
    onSuccess: () => invalidateKeys(queryClient, [...RECEIVABLE_KEYS, ...CONTACT_KEYS])
  });
}

export function useCancelReceivable() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await receivableAPI.cancel(id);
      return id;
    },
    onSuccess: () => invalidateKeys(queryClient, [...RECEIVABLE_KEYS, ...CONTACT_KEYS, ...TXN_RELATED_KEYS])
  });
}

export function useDeleteReceivable() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await receivableAPI.deletePermanent(id);
      return id;
    },
    onSuccess: () => invalidateKeys(queryClient, [...RECEIVABLE_KEYS, ...CONTACT_KEYS, ...TXN_RELATED_KEYS])
  });
}

export function useRecordReceivablePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data: paymentData }) => {
      const { data } = await receivableAPI.recordPayment(id, paymentData);
      return data.data;
    },
    onSuccess: () => invalidateKeys(queryClient, [...RECEIVABLE_KEYS, ...CONTACT_KEYS, ...TXN_RELATED_KEYS, 'payments'])
  });
}

export function useSettleReceivable() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data: settleData }) => {
      const { data } = await receivableAPI.settle(id, settleData);
      return data.data;
    },
    onSuccess: () => invalidateKeys(queryClient, [...RECEIVABLE_KEYS, ...CONTACT_KEYS, ...TXN_RELATED_KEYS, 'payments'])
  });
}

export function useSendReceivableReminder() {
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await receivableAPI.sendReminder(id);
      return data.data;
    }
  });
}

// ============ DEBT HOOKS ============

export function useDebtsQuery(params = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.debts(params),
    queryFn: async () => {
      const { data } = await debtAPI.getAll(params);
      return data.data;
    },
    placeholderData: keepPreviousData,
    ...options
  });
}

export function useDebtQuery(id, options = {}) {
  return useQuery({
    queryKey: queryKeys.debt(id),
    queryFn: async () => {
      const { data } = await debtAPI.getById(id);
      return data.data;
    },
    enabled: !!id,
    ...options
  });
}

export function useDebtsSummaryQuery(options = {}) {
  return useQuery({
    queryKey: queryKeys.debtsSummary,
    queryFn: async () => {
      const { data } = await debtAPI.getSummary();
      return data.data?.summary || data.data || {};
    },
    placeholderData: keepPreviousData,
    ...options
  });
}

export function useCreateDebt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (debtData) => {
      const { data } = await debtAPI.create(debtData);
      return data.data.debt;
    },
    onSuccess: () => invalidateKeys(queryClient, [...DEBT_KEYS, ...CONTACT_KEYS])
  });
}

export function useUpdateDebt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data: debtData }) => {
      const { data } = await debtAPI.update(id, debtData);
      return data.data.debt;
    },
    onSuccess: () => invalidateKeys(queryClient, [...DEBT_KEYS, ...CONTACT_KEYS])
  });
}

export function useCancelDebt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await debtAPI.cancel(id);
      return id;
    },
    onSuccess: () => invalidateKeys(queryClient, [...DEBT_KEYS, ...CONTACT_KEYS, ...TXN_RELATED_KEYS])
  });
}

export function useDeleteDebt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await debtAPI.deletePermanent(id);
      return id;
    },
    onSuccess: () => invalidateKeys(queryClient, [...DEBT_KEYS, ...CONTACT_KEYS, ...TXN_RELATED_KEYS])
  });
}

export function useRecordDebtPayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data: paymentData }) => {
      const { data } = await debtAPI.recordPayment(id, paymentData);
      return data.data;
    },
    onSuccess: () => invalidateKeys(queryClient, [...DEBT_KEYS, ...CONTACT_KEYS, ...TXN_RELATED_KEYS, 'payments'])
  });
}

export function useSettleDebt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data: settleData }) => {
      const { data } = await debtAPI.settle(id, settleData);
      return data.data;
    },
    onSuccess: () => invalidateKeys(queryClient, [...DEBT_KEYS, ...CONTACT_KEYS, ...TXN_RELATED_KEYS, 'payments'])
  });
}

export function useSendDebtReminder() {
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await debtAPI.sendReminder(id);
      return data.data;
    }
  });
}

// ============ PAYMENT HOOKS ============

export function usePaymentsQuery(params = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.payments(params),
    queryFn: async () => {
      const { data } = await paymentAPI.getAll(params);
      return data.data;
    },
    placeholderData: keepPreviousData,
    ...options
  });
}

export function usePaymentQuery(id, options = {}) {
  return useQuery({
    queryKey: queryKeys.payment(id),
    queryFn: async () => {
      const { data } = await paymentAPI.getById(id);
      return data.data.payment;
    },
    enabled: !!id,
    ...options
  });
}

export function useUpdatePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data: paymentData }) => {
      const { data } = await paymentAPI.update(id, paymentData);
      return data.data.payment;
    },
    onSuccess: () => invalidateKeys(queryClient, ['payments', 'payment', ...RECEIVABLE_KEYS, ...DEBT_KEYS, ...CONTACT_KEYS, ...TXN_RELATED_KEYS])
  });
}

export function useDeletePayment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await paymentAPI.delete(id);
      return id;
    },
    onSuccess: async () => {
      await invalidateKeys(queryClient, ['payments', 'payment', ...RECEIVABLE_KEYS, ...DEBT_KEYS, ...CONTACT_KEYS, ...TXN_RELATED_KEYS]);
    }
  });
}

// ============ NOTIFICATION HOOKS ============

export function useNotificationsQuery(params = {}, options = {}) {
  return useQuery({
    queryKey: queryKeys.notifications(params),
    queryFn: async () => {
      const { data } = await notificationAPI.getAll(params);
      return data.data;
    },
    placeholderData: keepPreviousData,
    ...options
  });
}

export function useNotificationQuery(id, options = {}) {
  return useQuery({
    queryKey: queryKeys.notification(id),
    queryFn: async () => {
      const { data } = await notificationAPI.getById(id);
      return data.data;
    },
    enabled: !!id,
    ...options
  });
}

export function useNotificationUnreadCountQuery(options = {}) {
  return useQuery({
    queryKey: queryKeys.notificationUnreadCount,
    queryFn: async () => {
      const { data } = await notificationAPI.getUnreadCount();
      return data.data.count;
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
    ...options
  });
}

export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await notificationAPI.markAsRead(id);
      return id;
    },
    onSuccess: () => invalidateKeys(queryClient, ['notifications', 'notification', 'notificationUnreadCount'])
  });
}

export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await notificationAPI.markAllAsRead();
    },
    onSuccess: () => invalidateKeys(queryClient, ['notifications', 'notification', 'notificationUnreadCount'])
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await notificationAPI.delete(id);
      return id;
    },
    onSuccess: () => invalidateKeys(queryClient, ['notifications', 'notification', 'notificationUnreadCount'])
  });
}

export function useClearAllNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await notificationAPI.clearAll();
      return response.data;
    },
    onSuccess: () => invalidateKeys(queryClient, ['notifications', 'notification', 'notificationUnreadCount'])
  });
}

// Notification Preferences
export function useNotificationPreferencesQuery(options = {}) {
  return useQuery({
    queryKey: ['notificationPreferences'],
    queryFn: async () => {
      const { data } = await notificationAPI.getPreferences();
      return data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await notificationAPI.updatePreferences(data);
      return response.data;
    },
    onSuccess: async () => {
      await invalidateKeys(queryClient, ['notificationPreferences']);
    }
  });
}

// Phone Verification
export function useSendPhoneVerification() {
  return useMutation({
    mutationFn: async (phone) => {
      const response = await notificationAPI.sendPhoneVerification(phone);
      return response.data;
    }
  });
}

export function useConfirmPhoneVerification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ phone, otp }) => {
      const response = await notificationAPI.confirmPhoneVerification(phone, otp);
      return response.data;
    },
    onSuccess: async () => {
      await invalidateKeys(queryClient, ['notificationPreferences']);
    }
  });
}

export function useRemovePhone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await notificationAPI.removePhone();
      return response.data;
    },
    onSuccess: async () => {
      await invalidateKeys(queryClient, ['notificationPreferences']);
    }
  });
}

// SMS Quota
export function useSmsQuotaQuery(options = {}) {
  return useQuery({
    queryKey: ['smsQuota'],
    queryFn: async () => {
      const { data } = await notificationAPI.getSmsQuota();
      return data.data;
    },
    staleTime: 60 * 1000, // 1 minute
    ...options
  });
}

